import {EditorView} from '@codemirror/view'
import type {ImageDimensions} from '@github-ui/image-utils'
import {getImageSizeFromFile, RETINA_PPI} from '@github-ui/image-utils'
import {noop} from '@github-ui/noop'
import {useSafeAsyncCallback} from '@github-ui/use-safe-async-callback'
import {useTrackingRef} from '@github-ui/use-tracking-ref'
import type {FileType, UnifiedFileSelectResult} from '@github-ui/use-unified-file-select'
import {useUnifiedFileSelect} from '@github-ui/use-unified-file-select'
import type React from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'

import {clearDropPosition, getDropPosition} from '../codemirror/extensions/drop-position'
import {
  addUploadPlaceholder,
  replaceUploadPlaceholder,
  updateUploadPlaceholder,
} from '../codemirror/extensions/upload-placeholder'
import {htmlImage} from '../language-tools/html/builders'
import {markdownImage, markdownLink} from '../language-tools/markdown/builders'
import type {HybridEditorTelemetry} from '../telemetry'

/** List of image MIME types which we do not attempt to render as images. */
const excludedImageMimeTypes = new Set(['image/bmp', 'image/tiff', 'image/heic', 'image/heif'])

const isImageFile = (file: File) =>
  file.type.toLowerCase().startsWith('image/') && !excludedImageMimeTypes.has(file.type.toLowerCase())
const isVideoFile = (file: File) => file.type.toLowerCase().startsWith('video/')

const normalizeUploadUrl = (url: string) => {
  const trimmed = url.trim()
  if (!trimmed) return trimmed

  if (typeof window === 'undefined' || !window.location?.origin) return trimmed

  try {
    return new URL(trimmed, window.location.origin).href
  } catch {
    return trimmed
  }
}

const sizedMarkdownImage = (dimensions: ImageDimensions, src: string, alt: string = 'Image') => {
  if (dimensions.pixelsPerInch === RETINA_PPI) {
    const width = Math.round(dimensions.width / 2)
    const height = Math.round(dimensions.height / 2)
    return htmlImage(alt, src, width, height)
  }

  if (dimensions && dimensions.width > 0 && dimensions.height > 0) {
    return htmlImage(alt, src, dimensions.width, dimensions.height)
  }

  return markdownImage(alt, src)
}

const markdown = (entry: PreparedUploadEntry, result: FileUploadResult, dimensions?: ImageDimensions | null) => {
  switch (entry.mediaKind) {
    case 'video':
      return result.url
    case 'image':
      return sizedMarkdownImage(dimensions || {width: 0, height: 0, pixelsPerInch: 1}, result.url, 'Image')
    case undefined:
      return markdownLink(result.file.name, result.url)
  }
}

interface FileHandler extends UnifiedFileSelectResult {
  errorMessage?: React.ReactNode
}

interface UseFileHandlingProps {
  repositoryId?: number
  view: EditorView | null
  disabled?: boolean
  value: string
  onUploadFile?: (file: File, onProgress?: (progressBytes: number) => void) => Promise<FileUploadResult>
  acceptedFileTypes?: FileType[]
  onFilesRejected?: (files: File[], rejectedTypes: string[]) => void
}

export interface FileUploadResult {
  /** The URL of the uploaded file. `null` if the upload failed (or reject the promise). */
  url: string
  /**
   * The file that was uploaded. Typically the client-side detected name, size, and content
   * type can be unreliable, so your file upload service may provide more accurate data. By
   * receiving an updated File instance with the more accurate data, the Markdown editor can
   * make better decisions.
   */
  file: File
}

type OptFileUploadResult = FileUploadResult | {url: null; file: File; error?: Error}

interface PreparedUploadEntry {
  id: string
  file: File
  previewUrl?: string
  mediaKind?: 'image' | 'video'
}

export const useFileHandler = (
  {value, view, disabled, onUploadFile, acceptedFileTypes, onFilesRejected}: UseFileHandlingProps,
  telemetry?: HybridEditorTelemetry,
): FileHandler | null => {
  const [errorMessage, setErrorMessage] = useState<React.ReactNode | undefined>(undefined)
  const placeholderIdRef = useRef(0)

  const viewRef = useTrackingRef(view)

  const errorVisibleForEnoughTimeRef = useRef(false)
  useEffect(() => {
    if (errorMessage) {
      errorVisibleForEnoughTimeRef.current = false
      const id = setTimeout(() => (errorVisibleForEnoughTimeRef.current = true), 1000)
      return () => clearTimeout(id)
    }
  }, [errorMessage])

  useEffect(() => {
    // clears the error message when the user types and enough time has passed

    if (errorVisibleForEnoughTimeRef.current) setErrorMessage(undefined)
  }, [value])

  const safeSetRejectedFiles = useSafeAsyncCallback((files: File[]) => {
    const types = new Set(
      files
        .map(({name}) => {
          const parts = name.split('.')
          return parts.length > 1 ? `.${parts.at(-1)}` : ''
        })
        .filter(s => s !== ''),
    )

    if (types.size > 0) {
      const typesList = [...types].join(', ')
      setErrorMessage(
        <>
          <span>
            File type<span>{types.size > 1 ? 's' : ''}</span> {typesList} not supported. See the{' '}
          </span>
          <a href="https://docs.github.com/en/get-started/writing-on-github/working-with-advanced-formatting/attaching-files">
            documentation
          </a>
          <span> for supported file types.</span>
        </>,
      )
      onFilesRejected?.(files, [...types])
    }
  })

  const updatePlaceholderProgress = useCallback(
    (entryId: string, progressPercent: number | undefined) => {
      if (viewRef.current) updateUploadPlaceholder(viewRef.current, {id: entryId, data: {progressPercent}})
    },
    [viewRef],
  )

  const insertPlaceholders = useCallback(
    (entries: PreparedUploadEntry[]) => {
      const editorView = viewRef.current
      if (!editorView || entries.length === 0) return

      // Determine where to insert the placeholder:
      // 1. Check if there's a drop position from a recent drop event (user dragged files)
      // 2. Fall back to the current selection start (user clicked upload button)
      let insertPos = getDropPosition(editorView.state)
      if (insertPos !== null) {
        // Clear the drop position so it's not reused for subsequent operations
        clearDropPosition(editorView)
      } else {
        // Fall back to the current selection start
        insertPos = editorView.state.selection.main.from
      }

      editorView.focus()

      for (const [index, entry] of entries.entries()) {
        addUploadPlaceholder(editorView, {
          id: entry.id,
          position: insertPos,
          order: index,
          data: {
            id: entry.id,
            fileName: entry.file.name,
            mimeType: entry.file.type,
            progressPercent: 0,
            previewUrl: entry.previewUrl,
            mediaKind: entry.mediaKind,
          },
        })
      }
    },
    [viewRef],
  )

  // It's crucial that this is done safely because file uploads can take a long time - there's
  // a very good chance that the references will be outdated or the component unmounted by the time this is called.
  const safeHandleCompletedFileUpload = useSafeAsyncCallback(
    (entry: PreparedUploadEntry, result: OptFileUploadResult, dimensions: ImageDimensions | null) => {
      const editorView = viewRef.current
      if (!editorView) return

      // even if there was an error, we still need to remove the placeholder
      const replacement = result.url ? markdown(entry, result, dimensions) : ''

      replaceUploadPlaceholder(editorView, entry.id, replacement)

      if (!replacement) {
        setErrorMessage(
          'error' in result && result.error !== undefined
            ? `File upload failed: ${result.error.message}`
            : 'File upload failed',
        )
      }

      // Clean up blob URL
      if (entry.previewUrl) {
        URL.revokeObjectURL(entry.previewUrl)
      }
    },
  )

  const uploadFiles = useCallback(
    (entries: PreparedUploadEntry[]): Array<Promise<void>> =>
      entries.map(async entry => {
        // Provide a progress callback for the uploader to track upload progress
        const progressCallback = (progressBytes: number) => {
          const percent = entry.file.size > 0 ? Math.round((progressBytes / entry.file.size) * 100) : 0
          updatePlaceholderProgress(entry.id, Math.min(percent, 99)) // Cap at 99% until complete
        }

        let result: OptFileUploadResult = {url: null, file: entry.file}
        try {
          result = (await onUploadFile?.(entry.file, progressCallback)) ?? {file: entry.file, url: null}
        } catch (error) {
          result = {file: entry.file, url: null, error: error instanceof Error ? error : undefined}

          // rethrow asynchronously so the error is reported and logged
          setTimeout(() => {
            throw error
          })
        }

        let dimensions: ImageDimensions | null = null
        if (result.url && (entry.mediaKind === 'image' || isImageFile(result.file))) {
          try {
            dimensions = await getImageSizeFromFile(result.file)
          } catch (error) {
            dimensions = {width: 0, height: 0, pixelsPerInch: 1}
            setTimeout(() => {
              throw error
            })
          }
        }

        result.url &&= normalizeUploadUrl(result.url)

        telemetry?.trackFileUpload({
          phase: result.url ? 'succeeded' : 'failed',
          mimeType: entry.file.type || 'unknown',
          sizeBytes: entry.file.size,
          mediaKind: entry.mediaKind ?? 'other',
          errorType: result.url ? undefined : 'upload',
        })

        safeHandleCompletedFileUpload(entry, result, dimensions)
      }),
    [onUploadFile, safeHandleCompletedFileUpload, telemetry, updatePlaceholderProgress],
  )

  const onSelectFiles = useCallback(
    async (accepted: File[], rejected: File[]) => {
      if (accepted.length > 0) {
        const total = accepted.length
        const entries: PreparedUploadEntry[] = accepted.map((file, index) => {
          const id = `upload-placeholder-${placeholderIdRef.current++}`
          const mediaKind = isVideoFile(file) ? 'video' : isImageFile(file) ? 'image' : undefined
          const previewUrl = mediaKind ? URL.createObjectURL(file) : undefined

          return {
            id,
            file,
            order: index + 1,
            total,
            previewUrl,
            mediaKind,
          }
        })

        for (const entry of entries) {
          telemetry?.trackFileUpload({
            phase: 'selected',
            mimeType: entry.file.type || 'unknown',
            sizeBytes: entry.file.size,
            mediaKind: entry.mediaKind ?? 'other',
          })
        }

        insertPlaceholders(entries)

        await Promise.allSettled(uploadFiles(entries))
      }
      // setting rejected files will hide upload progress, replacing it with an error message
      // so only call it after successful files are uploaded
      safeSetRejectedFiles(rejected)
    },
    [safeSetRejectedFiles, insertPlaceholders, telemetry, uploadFiles],
  )

  let fileSelect = useUnifiedFileSelect({
    acceptedFileTypes,
    multi: true,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onSelect: onSelectFiles,
  })

  if (disabled || !view) {
    fileSelect = {
      clickTargetProps: {
        onClick: noop,
      },
      dropTargetProps: {
        onDragEnter: noop,
        onDragLeave: noop,
        onDrop: noop,
        onDragOver: noop,
      },
      pasteTargetProps: {
        onPaste: noop,
      },
      isDraggedOver: false,
    }
  }

  return onUploadFile
    ? {
        ...fileSelect,
        errorMessage,
      }
    : null
}
